import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Badge from "../shared/Badge";
import PriceDisplay from "../shared/PriceDisplay";
import RatingStars from "../shared/RatingStars";
import { fetchProductBySlugOrId } from "../../api/products";
import { useProductViewer } from "../../hooks/useProductViewer";
import { displayShopName } from "../../utils/shopName";
import { productShopPath } from "../../utils/shopPath";
import { isInStock, lowStockCount, maxQuantity } from "../../utils/stock";
import VerifiedBadge from "../shared/VerifiedBadge";
import "./ProductInfo.css";

// Stock wording ("✓ In stock", "Only N left", "Out of stock") comes from
// utils/stock.js, shared with the card, spec table and wishes.

// Builds a short readable label from whichever dimensions this variant
// actually has set — e.g. "Black · Size 42" or "Black · Size 42 · Material: Leather".
export const variantLabel = (v) => {
  const parts = [];
  if (v.color) parts.push(v.color);
  if (v.size) parts.push(`Size ${v.size}`);
  if (v.attribute_value) parts.push(v.attribute_label ? `${v.attribute_label}: ${v.attribute_value}` : v.attribute_value);
  return parts.join(" · ");
};

const uniq = (arr) => [...new Set(arr.filter(Boolean))];

// One row per differentiating dimension (Color / Size / custom attribute),
// each a row of selectable chips — e.g. "Color: Black White Brown" then
// "Size: 40 41 42 43" — instead of listing every combination as its own
// full-width row. Picking a chip resolves to the matching variant and that
// becomes the selection. See CLAUDE.md "Product Variants".
//
// `pending` tracks the user's in-progress picks independently of the last
// *committed* selection (`selected`), updated via the functional setState
// form. Building the next combination from `selected.color`/`selected.size`
// directly was the original bug: picking color then size in succession could
// read the OLD color before that click had committed, resolving to the
// wrong SKU (e.g. picking White then Size 43 added Black/43 to the cart).
const VariantPicker = ({ variants, resetKey, selected, onSelect, onUnavailable }) => {
  const [pending, setPending] = useState({
    color: selected.color, size: selected.size, attribute_value: selected.attribute_value,
  });

  useEffect(() => {
    setPending({ color: selected.color, size: selected.size, attribute_value: selected.attribute_value });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  if (!variants || variants.length < 2) return null;

  const colors = uniq(variants.map((v) => v.color));
  const sizes  = uniq(variants.map((v) => v.size));
  const attrLabel  = variants.find((v) => v.attribute_label)?.attribute_label;
  const attrValues = attrLabel ? uniq(variants.map((v) => v.attribute_value)) : [];

  const findMatch = (color, size, attrValue) =>
    variants.find((v) =>
      (v.color || null) === (color || null) &&
      (v.size || null) === (size || null) &&
      (v.attribute_value || null) === (attrValue || null)
    );

  const pick = (dimension, value) => {
    setPending((prev) => {
      const next = { ...prev, [dimension === "attr" ? "attribute_value" : dimension]: value };
      const match = findMatch(next.color, next.size, next.attribute_value);
      if (match) onSelect(match);
      else onUnavailable();
      return next;
    });
  };

  const axes = [
    colors.length > 1 && { key: "color", label: "Color", options: colors, current: pending.color },
    sizes.length > 1 && { key: "size", label: "Size", options: sizes, current: pending.size },
    attrValues.length > 1 && { key: "attr", label: attrLabel, options: attrValues, current: pending.attribute_value },
  ].filter(Boolean);

  if (axes.length === 0) return null;

  const currentMatch = findMatch(pending.color, pending.size, pending.attribute_value);
  const inStock = currentMatch ? isInStock(currentMatch) : false;

  return (
    <div className="pi-variants">
      {axes.map((axis) => (
        <div className="pi-variant-axis" key={axis.key}>
          <span className="pi-variants-label">{axis.label}</span>
          <div className="pi-chip-row" role="radiogroup" aria-label={axis.label}>
            {axis.options.map((opt) => (
              <button
                key={opt}
                type="button"
                role="radio"
                aria-checked={axis.current === opt}
                className={`pi-chip${axis.current === opt ? " pi-chip--active" : ""}`}
                onClick={() => pick(axis.key, opt)}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>
      ))}
      {currentMatch ? (
        <span className={`pi-variant-stock${inStock ? "" : " pi-variant-stock--oos"}`}>
          {inStock ? "In stock" : "Out of stock"}
        </span>
      ) : (
        <span className="pi-variant-stock pi-variant-stock--oos">
          This combination isn't available
        </span>
      )}
    </div>
  );
};

const ProductInfo = ({ product, onAddToCart, onSelectionChange }) => {
  const viewer = useProductViewer();
  const [qty, setQty] = useState(1);
  const [selected, setSelected] = useState(product);
  const [addError, setAddError] = useState("");
  const [unavailable, setUnavailable] = useState(false);
  const [adding, setAdding] = useState(false);

  // Reset the picker whenever the underlying product page changes (e.g.
  // navigating to a different item entirely).
  useEffect(() => { setSelected(product); setQty(1); setUnavailable(false); }, [product]);

  useEffect(() => { onSelectionChange?.(selected); }, [selected, onSelectionChange]);

  const inStock  = isInStock(selected);
  const lowLeft  = inStock ? lowStockCount(selected) : null;   // "Only N left", only when real
  const lowStock = lowLeft != null;
  const maxQty   = maxQuantity(selected);

  const hasVariantPicker = product.variants && product.variants.length >= 2;
  // The seller's public name: seller_display_name from the shop-pages API
  // (display name, else "@handle"; never the legal name), falling back to
  // the fields the API sends before that release.
  const sellerLabel = product.seller_display_name
    || (product.seller_shop_name ? displayShopName(product.seller_shop_name) : product.seller_name)
    || null;
  // The shop's page (/shop/<handle>); none for a seller without a handle.
  const sellerPath = productShopPath(product);
  const reviewCount = product.review_count || 0;
  const isClearance = product.tag === "Clearance";

  const handleAddToCart = async () => {
    if (adding) return; // guard against duplicate submits (e.g. double-click)
    setAddError("");
    setAdding(true);
    try {
      if (selected.id === product.id) {
        onAddToCart(product, qty);
      } else {
        // Selected a sibling variant — its summary doesn't carry every field
        // (category, weight_kg, is_preorder, ...) the cart/checkout rely on,
        // so fetch the full record for that one before adding. As the
        // signed-in shopper, like the page itself: a sibling of an
        // age-restricted product is a 403 when fetched anonymously.
        const full = await fetchProductBySlugOrId(selected.id, { headers: viewer.headers });
        onAddToCart(full, qty);
      }
    } catch {
      setAddError("Could not add that option — please try again.");
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="pi-wrap">
      {/* Category breadcrumb */}
      <p className="pi-category">{product.category}</p>

      {/* Badges row — merchandising tags only; stock status has its own row below */}
      <div className="pi-badges">
        {product.tag && <Badge label={product.tag} />}
        {product.is_preorder && <Badge label="Pre-Order" variant="preorder" />}
      </div>

      {/* Title. No wish heart here: the cards carry it (owner, 2026-10-24). */}
      <h1 className="pi-title">{product.name}</h1>

      {/* Rating — real backend-computed average_rating/review_count, links to
          Reviews section. A product with no verified-purchase reviews shows
          nothing at all here, the same as its card (CLAUDE.md "Ratings are
          real or absent"); the Reviews section below does the inviting. Not
          an empty or visually-hidden link: it would still take focus, and a
          keyboard user would land on a target they can't see. */}
      {reviewCount > 0 && (
        <a href="#rv-heading" className="pi-rating-row">
          <RatingStars rating={product.average_rating} size="sm" showNumber />
          <span className="pi-review-count">· {reviewCount} review{reviewCount !== 1 ? "s" : ""}</span>
        </a>
      )}

      {hasVariantPicker && (
        <VariantPicker
          variants={product.variants}
          resetKey={product.id}
          selected={selected}
          onSelect={(v) => { setSelected(v); setQty(1); setAddError(""); setUnavailable(false); }}
          onUnavailable={() => setUnavailable(true)}
        />
      )}

      {/* Price */}
      <div className="pi-price-row">
        <PriceDisplay
          price={selected.price}
          compareAtPrice={selected.compare_at_price}
          size="lg"
        />
      </div>

      {/* Inventory-backed availability */}
      <p
        className={`pi-stock-line ${inStock ? (product.is_preorder ? "pi-stock-ok" : lowStock ? "pi-stock-low" : "pi-stock-ok") : "pi-stock-out"}`}
        role={inStock ? "status" : "alert"}
      >
        {inStock ? (product.is_preorder ? "Available to pre-order" : lowStock ? `Only ${lowLeft} left` : "✓ In stock") : "Out of stock"}
      </p>

      {/* Quantity + CTA — constrained to same width */}
      <div className="pi-add-group">
        <div className="pi-qty-row">
          <label className="pi-qty-label" htmlFor="pi-qty">Quantity</label>
          <div className={`pi-qty-ctrl${!inStock ? " pi-qty-ctrl--disabled" : ""}`}>
            <button
              type="button"
              className="pi-qty-btn"
              aria-label="Decrease quantity"
              disabled={!inStock || qty <= 1}
              onClick={() => setQty((q) => Math.max(1, q - 1))}
            >−</button>
            <input
              id="pi-qty"
              type="number"
              className="pi-qty-input"
              value={qty}
              min={1}
              max={maxQty}
              readOnly
              disabled={!inStock}
              aria-label="Quantity"
            />
            <button
              type="button"
              className="pi-qty-btn"
              aria-label="Increase quantity"
              disabled={!inStock || qty >= maxQty}
              onClick={() => setQty((q) => Math.min(maxQty, q + 1))}
            >+</button>
          </div>
        </div>

        {/* CTA */}
        <button
          type="button"
          className={`pi-cart-btn pi-cart-btn--full${adding ? " pi-cart-btn--loading" : ""}`}
          disabled={!inStock || unavailable || adding}
          onClick={handleAddToCart}
          aria-label={`Add ${product.name} to cart`}
        >
          {product.is_preorder ? "Pre-Order" : adding ? "Adding…" : "Add to Cart"}
        </button>
      </div>{/* end pi-add-group */}

      {addError && <p className="pi-oos-msg" role="alert">{addError}</p>}

      {product.is_preorder && (
        <p className="pi-preorder-notice" role="status">
          This is a pre-order item shipped from abroad — please allow up to 2 weeks for delivery.
        </p>
      )}

      {!inStock && (
        <p className="pi-oos-msg" role="alert">
          This item is currently out of stock.
        </p>
      )}

      {/* Trust information */}
      <p className="pi-trust-row">
        <span>Secure payment</span>
        <span className="pi-attr-sep" aria-hidden="true">·</span>
        {isClearance ? (
          <span>Final sale — not eligible for return</span>
        ) : (
          <a href="/returns" className="pi-trust-link">14-day returns</a>
        )}
      </p>

      {/* Seller — beneath trust information; omitted entirely for legacy products with no seller */}
      {sellerLabel && (
        <p className="pi-seller-row">
          Sold by{" "}
          {/* Name and seal share one unbreakable span: the seal sits straight
              after the shop name and never wraps onto a line of its own. */}
          {sellerPath ? (
            <Link to={sellerPath} className="pi-seller-id pi-seller-link">
              <span className="pi-seller-name">
                {sellerLabel}
              </span>
              {product.seller_verified && <VerifiedBadge />}
            </Link>
          ) : (
            <span className="pi-seller-id">
              <span className="pi-seller-name">
                {sellerLabel}
              </span>
              {product.seller_verified && <VerifiedBadge />}
            </span>
          )}
        </p>
      )}
    </div>
  );
};

export default ProductInfo;
